import {
  GENERATION_PROJECT_DUTY_HASH_VERSION,
  GENERATION_PROJECT_DUTY_MODEL_VERSION,
  GENERATION_PROJECT_DUTY_SCHEMA_VERSION,
  type EngineeringVerdict,
  type GenerationAmbientCorrectionPoint,
  type GenerationImpactScenarioInput,
  type GenerationNoiseUnit,
  type GenerationProjectDutyBasis,
  type GenerationProjectDutyCompleteness,
  type GenerationProjectDutyExhibitBasis,
  type GenerationProjectDutySource,
  type GenerationProjectDutySpatialSummary,
  type GenerationProjectFuelGasDuty,
  type GenerationProjectInstalledReservations,
  type GenerationProjectReservationEnvelope,
  type NoiseDatum,
  type PublicGenerationProjectDutyExhibitBasis,
} from '../../types/generationImpact';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import {
  applyGenerationEquipmentEnvelope,
  validateGenerationEquipmentBasis,
} from './generationEquipmentCatalog';
import { expectedUserEquipmentDutyFingerprint } from './userEquipmentEnvelope';
import { firmGasDeliverableDthPerDay } from './gasInterconnectDerate';
import { SQUARE_METRES_PER_ACRE } from './generationTestFit';

const MAX_STRING_LENGTH = 2_000;
const MAX_SOURCE_COUNT = 25;
const MAX_SOURCE_ID_COUNT = 25;
const MAX_AMBIENT_POINT_COUNT = 100;
const MAX_BLOCK_COUNT = 1_000;
const MAX_DIMENSION_M = 100_000;
const MAX_POWER_MW = 100_000;
const MAX_HEAT_RATE_BTU_PER_KWH = 100_000;
const MAX_DAILY_WATER_GAL = 10_000_000_000;
/**
 * Upper bound for a daily gross-generation reference, DERIVED rather than
 * guessed: the platform's maximum plant rating run flat out for 24 hours.
 *
 * This field previously reused `MAX_DAILY_WATER_GAL` — a bound in GALLONS
 * applied to a quantity in MWh/day. It never rejected anything real (it was
 * ~4,000x too permissive), but a gallons constant guarding an energy quantity
 * is exactly the unit-category confusion the codebase's conversion rules exist
 * to prevent, and it would have silently accepted an impossible figure.
 */
const HOURS_PER_DAY = 24;
const MAX_DAILY_GROSS_MWH = MAX_POWER_MW * HOURS_PER_DAY;
const MAX_PRESSURE_KPA = 100_000;
const MAX_ACOUSTIC_SOURCE_COUNT = 100;
const RELATIVE_TOLERANCE = 1e-9;
const MAX_GAS_CAPACITY_DTH_PER_DAY = 10_000_000;
const MAX_HHV_BTU_PER_SCF = 2_000;
const MAX_COMPRESSIBILITY = 2;

/**
 * A documented interconnect writes `availableGasCapacityDthPerDay` ONLY when
 * the quantity is contractually firm.
 *
 * The solver and equipment ranking both read that field as firm, site-
 * deliverable capacity. A scoping / ROM-class estimate is real evidence worth
 * recording and derating, but promoting it to firm would silence the honest
 * `gas-capacity-mismatch` amber and present an indicative number as contracted
 * transport — the failure mode the extraction gate already refuses on the
 * automatic path.
 */
function appliesFirmGasCapacity(
  fuelGas: GenerationProjectFuelGasDuty | undefined,
): boolean {
  return fuelGas?.supply?.quantityBasis === 'firm-transport-agreement'
    && firmGasDeliverableDthPerDay(fuelGas.supply) != null;
}

export type GenerationProjectDutyBasisDraft = Omit<
  GenerationProjectDutyBasis,
  | 'schemaVersion'
  | 'modelVersion'
  | 'hashVersion'
  | 'catalogBinding'
  | 'restorationBaseline'
  | 'basisHash'
  | 'appliedDutyFingerprint'
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function finiteInRange(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= minimum
    && value <= maximum;
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(1, Math.abs(left), Math.abs(right))
    * RELATIVE_TOLERANCE;
}

function boundedString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_STRING_LENGTH;
}

function opaqueSourceId(value: unknown): value is string {
  return typeof value === 'string'
    && /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value);
}

function dateString(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.valueOf())
    && parsed.toISOString().slice(0, 10) === value;
}

function sortedUnique(values: readonly string[]): boolean {
  return values.every((value, index) => (
    index === 0 || values[index - 1]! < value
  ));
}

function physicalBlockCount(input: GenerationImpactScenarioInput): number {
  return input.units.reduce(
    (sum, unit) => sum + (Number.isInteger(unit.count) && unit.count > 0 ? unit.count : 0),
    0,
  );
}

function basisHashPayload(value: GenerationProjectDutyBasis): unknown {
  const {
    basisHash: _basisHash,
    ...payload
  } = value;
  return payload;
}

/** Canonical inner authority hash, independent of the scenario's outer v1 hash. */
export function hashGenerationProjectDutyBasis(value: GenerationProjectDutyBasis): string {
  return hashCanonicalJson(basisHashPayload(value));
}

function applicationFingerprintPayload(
  input: GenerationImpactScenarioInput,
  basis: GenerationProjectDutyBasis | undefined,
): unknown {
  const performance = basis?.performance;
  const installed = basis?.installedReservations;
  return {
    appliedBlockCount: physicalBlockCount(input),
    ...(performance ? {
      units: input.units.map(unit => ({
        ...(performance.referenceIsoGrossMWPerBlock == null
          ? {} : { isoGrossMWPerUnit: unit.isoGrossMWPerUnit }),
        ...(performance.ambientCorrection == null
          ? {} : { ambientFactor: unit.ambientFactor }),
        ...(performance.degradationFactor == null
          ? {} : { degradationFactor: unit.degradationFactor }),
        ...(performance.auxiliaryLoadMWPerBlock == null
          ? {} : { auxiliaryLoadMWPerUnit: unit.auxiliaryLoadMWPerUnit }),
        ...(performance.heatRateBtuPerKWhHhv == null
          ? {} : { heatRateBtuPerKWhHhv: unit.heatRateBtuPerKWhHhv }),
        ...(performance.forcedOutageRate == null
          ? {} : { forcedOutageRate: unit.forcedOutageRate ?? null }),
      })),
    } : {}),
    ...(basis?.acoustics ? { noiseSources: input.noiseSources ?? null } : {}),
    ...(basis?.water?.kind === 'gross-output-intensity' ? {
      water: {
        generationWithdrawalGalPerMWhGross:
          input.water.generationWithdrawalGalPerMWhGross,
        generationConsumptionGalPerMWhGross:
          input.water.generationConsumptionGalPerMWhGross,
        generationDesignDay: input.water.generationDesignDay ?? null,
      },
    } : basis?.water?.kind === 'absolute-design-day' ? {
      water: { generationDesignDay: input.water.generationDesignDay ?? null },
    } : {}),
    // Gated on the dossier actually applying a firm capacity, exactly like the
    // acoustics / water / installed branches above. A dossier without
    // `fuelGas.supply` contributes nothing, so every persisted dossier keeps
    // its fingerprint byte-for-byte.
    ...(appliesFirmGasCapacity(basis?.fuelGas) ? {
      availableGasCapacityDthPerDay: input.availableGasCapacityDthPerDay ?? null,
    } : {}),
    ...(installed ? {
      footprint: {
        installedPlantAcres: input.footprint.installedPlantAcres ?? null,
        ...(installed.coolingEnvelope
          ? { coolingAcres: input.footprint.coolingAcres } : {}),
        ...(installed.switchyardEnvelope
          ? { switchyardAcres: input.footprint.switchyardAcres } : {}),
      },
      ccsFootprint: {
        ...(installed.ccsCaptureEnvelope
          ? { footprintAcres: input.ccs.footprintAcres } : {}),
        ...(installed.ccsCompressionEnvelope
          ? { compressionFootprintAcres: input.ccs.compressionFootprintAcres } : {}),
      },
    } : {}),
  };
}

/** Hash only fields that application of a project dossier may change. */
export function hashGenerationProjectDutyApplication(
  input: GenerationImpactScenarioInput,
  basis: GenerationProjectDutyBasis | undefined = input.projectDutyBasis,
): string {
  return hashCanonicalJson(applicationFingerprintPayload(input, basis));
}

function assertAmbientCurve(points: readonly GenerationAmbientCorrectionPoint[]): void {
  if (points.length < 2 || points.length > MAX_AMBIENT_POINT_COUNT) {
    throw new RangeError(
      `Ambient correction curve must contain between 2 and ${MAX_AMBIENT_POINT_COUNT} points.`,
    );
  }
  points.forEach((point, index) => {
    if (!finiteInRange(point.dryBulbC, -100, 100)) {
      throw new RangeError(`Ambient correction point ${index + 1} dry bulb must be between -100 and 100 °C.`);
    }
    if (!finiteInRange(point.grossOutputFactor, 0.000_001, 1)) {
      throw new RangeError(`Ambient correction point ${index + 1} output factor must be in (0, 1].`);
    }
    if (index > 0 && point.dryBulbC <= points[index - 1]!.dryBulbC) {
      throw new RangeError('Ambient correction curve temperatures must be strictly increasing with no duplicates.');
    }
  });
}

/**
 * Piecewise-linear interpolation inside the documented OEM/EPC temperature
 * range. Endpoints are exact; clamping and extrapolation are prohibited.
 */
export function interpolateAmbientOutputFactor(
  points: readonly GenerationAmbientCorrectionPoint[],
  designDryBulbC: number,
): number {
  assertAmbientCurve(points);
  if (!finiteInRange(designDryBulbC, -100, 100)) {
    throw new RangeError('Design dry bulb must be finite and between -100 and 100 °C.');
  }
  const first = points[0]!;
  const last = points.at(-1)!;
  if (designDryBulbC < first.dryBulbC || designDryBulbC > last.dryBulbC) {
    throw new RangeError(
      `Design dry bulb ${designDryBulbC} °C is outside the documented ambient curve range ${first.dryBulbC} to ${last.dryBulbC} °C; extrapolation is not permitted.`,
    );
  }
  const exact = points.find(point => point.dryBulbC === designDryBulbC);
  if (exact) return exact.grossOutputFactor;
  for (let index = 1; index < points.length; index += 1) {
    const upper = points[index]!;
    const lower = points[index - 1]!;
    if (designDryBulbC < upper.dryBulbC) {
      const fraction = (designDryBulbC - lower.dryBulbC)
        / (upper.dryBulbC - lower.dryBulbC);
      return lower.grossOutputFactor
        + fraction * (upper.grossOutputFactor - lower.grossOutputFactor);
    }
  }
  return last.grossOutputFactor;
}

function areaAcres(envelope: GenerationProjectReservationEnvelope | undefined): number {
  return envelope ? envelope.lengthM * envelope.widthM / SQUARE_METRES_PER_ACRE : 0;
}

/** Coordinate-free reserved-area schedule used by UI and report surfaces. */
export function generationProjectDutySpatialSummary(
  installed: GenerationProjectInstalledReservations,
  ccsEnabled = true,
): GenerationProjectDutySpatialSummary {
  const permanentEquipmentAcres = installed.blockQuantity
    * areaAcres(installed.generationBlockEnvelope)
    + areaAcres(installed.coolingEnvelope)
    + areaAcres(installed.switchyardEnvelope)
    + (ccsEnabled ? areaAcres(installed.ccsCaptureEnvelope) : 0)
    + (ccsEnabled ? areaAcres(installed.ccsCompressionEnvelope) : 0);
  const routeReservationAcres = areaAcres(installed.naturalGasRouteEnvelope)
    + (ccsEnabled ? areaAcres(installed.co2ExportRouteEnvelope) : 0);
  const constructionLaydownAcres = areaAcres(installed.constructionLaydownEnvelope);
  return {
    permanentEquipmentAcres,
    routeReservationAcres,
    constructionLaydownAcres,
    totalReservedAcres:
      permanentEquipmentAcres + routeReservationAcres + constructionLaydownAcres,
  };
}

function acousticNoiseSources(
  basis: GenerationProjectDutyBasis,
): GenerationNoiseUnit[] | undefined {
  const rows = basis.acoustics?.acousticSources;
  if (!rows) return undefined;
  return rows.map(source => ({
    id: `project-duty:${source.id}`,
    name: source.label,
    count: source.quantity.scope === 'site-total'
      ? source.quantity.count
      : source.quantity.count * basis.installedReservations.blockQuantity,
    noise: structuredClone(source.appliedDatum),
  }));
}

function sourceCitation(sources: readonly GenerationProjectDutySource[]): string {
  return sources.map(source => source.disclosure === 'public'
    ? source.url
    : `Private ${source.authorityRole} project source retained in the hash-bound duty dossier.`)
    .join(' | ');
}

function replaceProjectDutyAssumptions(
  input: GenerationImpactScenarioInput,
  basis: GenerationProjectDutyBasis,
): void {
  const retained = input.assumptions.filter(
    assumption => !assumption.field.startsWith('projectDuty'),
  );
  const citation = sourceCitation(basis.sources);
  input.assumptions = [
    ...retained,
    {
      field: 'projectDutyAuthority',
      value: basis.basisName,
      source: 'vendor',
      label: 'Hash-bound project OEM/EPC duty dossier',
      citation,
      confidence: 'high',
    },
    {
      field: 'projectDutyInstalledPlant',
      value: generationProjectDutySpatialSummary(
        basis.installedReservations,
        input.ccs.enabled,
      ).totalReservedAcres,
      source: 'vendor',
      label: 'Project installed, route, and construction reservation area in acres',
      citation,
      confidence: basis.installedReservations.generationBlockEnvelope.maintenanceClearanceIncluded
        && basis.installedReservations.generationBlockEnvelope.fireAccessIncluded
        ? 'high'
        : 'medium',
    },
  ];
}

function finalizedBasis(
  source: GenerationImpactScenarioInput,
  draft: GenerationProjectDutyBasisDraft,
): GenerationProjectDutyBasis {
  // A dossier binds to whichever equipment authority the scenario carries: an
  // immutable catalog envelope, or a user-supplied entry that is currently the
  // applied duty. The user path is what lets the machine a project is actually
  // BUYING carry its own vendor data; it does not upgrade that entry's evidence
  // tier, which stays unverified.
  const equipment = source.equipmentBasis;
  const userBinding = equipment ? null : resolveUserEquipmentBinding(source);
  if (!equipment && !userBinding) {
    throw new RangeError('Apply an equipment envelope — catalog or user-supplied — before applying a project OEM/EPC duty dossier.');
  }
  if (equipment) {
    const equipmentErrors = validateGenerationEquipmentBasis(equipment);
    if (equipmentErrors.length > 0) {
      throw new RangeError(`The selected equipment catalog basis is invalid: ${equipmentErrors.join('; ')}`);
    }
  }
  const performance = draft.performance
    ? structuredClone(draft.performance)
    : undefined;
  if (performance?.ambientCorrection) {
    performance.ambientCorrection.appliedGrossOutputFactor = interpolateAmbientOutputFactor(
      performance.ambientCorrection.points,
      performance.ambientCorrection.designDryBulbC,
    );
  }
  const basis: GenerationProjectDutyBasis = {
    schemaVersion: GENERATION_PROJECT_DUTY_SCHEMA_VERSION,
    modelVersion: GENERATION_PROJECT_DUTY_MODEL_VERSION,
    hashVersion: GENERATION_PROJECT_DUTY_HASH_VERSION,
    basisId: draft.basisId,
    basisName: draft.basisName,
    // Exactly one binding. A catalog dossier writes the SAME key it always has,
    // so persisted dossiers keep re-deriving byte-identically.
    ...(equipment
      ? {
          catalogBinding: {
            catalogId: equipment.catalogId,
            catalogAppliedDutyFingerprint: equipment.appliedDutyFingerprint,
          },
        }
      : {
          userEquipmentBinding: {
            entryId: userBinding!.entryId,
            appliedDutyFingerprint: userBinding!.appliedDutyFingerprint,
          },
        }),
    restorationBaseline: {
      ccsCaptureFootprintAcres: source.ccs.footprintAcres,
      ccsCompressionFootprintAcres: source.ccs.compressionFootprintAcres,
      // Only recorded when this dossier actually writes the gas capacity, so a
      // pre-existing dossier keeps a two-key baseline and hashes identically.
      // `null` means "was undocumented", which restores by DELETING the field
      // rather than zeroing it.
      ...(appliesFirmGasCapacity(draft.fuelGas)
        ? { availableGasCapacityDthPerDay: source.availableGasCapacityDthPerDay ?? null }
        : {}),
    },
    ...(performance ? { performance } : {}),
    installedReservations: structuredClone(draft.installedReservations),
    ...(draft.fuelGas ? { fuelGas: structuredClone(draft.fuelGas) } : {}),
    ...(draft.acoustics ? { acoustics: structuredClone(draft.acoustics) } : {}),
    ...(draft.water ? { water: structuredClone(draft.water) } : {}),
    sources: [...structuredClone(draft.sources)].sort((left, right) => (
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0
    )),
    basisHash: '',
    appliedDutyFingerprint: '',
  };
  return basis;
}

/**
 * Strip an applied dossier back to its authored draft.
 *
 * Every field removed here is DERIVED at apply time — the version stamps, the
 * catalog binding, the pre-dossier restoration baseline and both hashes. Round-
 * tripping a basis through this and back through `applyGenerationProjectDutyBasis`
 * therefore reproduces it exactly against an unchanged scenario, and re-derives
 * it correctly against a changed one.
 */
/**
 * The user-supplied entry currently supplying the scenario's duty, with the
 * fingerprint a dossier binds to. Null unless the applied entry both exists and
 * still reproduces the scenario's units — a hand-edited fleet is not a binding
 * target, exactly as a modified catalog envelope is not.
 */
function resolveUserEquipmentBinding(
  source: GenerationImpactScenarioInput,
): { entryId: string; appliedDutyFingerprint: string } | null {
  const entryId = source.equipmentWorkspace?.appliedUserEquipmentId;
  if (!entryId) return null;
  const entry = source.equipmentWorkspace?.userEquipment
    .find(candidate => candidate.id === entryId);
  if (!entry) return null;
  const blockCount = physicalBlockCount(source);
  if (!Number.isInteger(blockCount) || blockCount < 1) return null;
  return {
    entryId,
    appliedDutyFingerprint: expectedUserEquipmentDutyFingerprint(entry, blockCount),
  };
}

export function generationProjectDutyBasisDraft(
  basis: GenerationProjectDutyBasis,
): GenerationProjectDutyBasisDraft {
  const {
    schemaVersion: _schemaVersion,
    modelVersion: _modelVersion,
    hashVersion: _hashVersion,
    catalogBinding: _catalogBinding,
    restorationBaseline: _restorationBaseline,
    basisHash: _basisHash,
    appliedDutyFingerprint: _appliedDutyFingerprint,
    ...draft
  } = structuredClone(basis);
  return draft;
}

/**
 * Carry an applied dossier across a BLOCK-COUNT change.
 *
 * `installedReservations.blockQuantity` is not a user field — it mirrors the
 * scenario fleet and is hard-checked on apply, on validate and again server-
 * side. Changing the block count therefore used to DELETE the dossier: every
 * field, every evidence source and all eight envelope L×W pairs, discarded
 * because one derived integer no longer matched.
 *
 * Nothing in the dossier actually depends on the count except that integer and
 * the `installedPlantAcres` scalar derived from it, so the honest operation is
 * a re-derivation, not a deletion. Re-authoring the draft at the new quantity
 * and pushing it back through the ONE apply path keeps every downstream
 * invariant — binding fingerprint, restoration baseline, both hashes and the
 * full validator — computed by the same code that computes them normally.
 *
 * Returns the source unchanged when there is nothing to carry. Throws only if
 * the re-application is genuinely invalid, which the caller reports rather than
 * silently swallowing.
 */
export function rescaleGenerationProjectDutyBasis(
  source: GenerationImpactScenarioInput,
  carried: GenerationProjectDutyBasis,
  blockCount: number,
): GenerationImpactScenarioInput {
  const draft = generationProjectDutyBasisDraft(carried);
  draft.installedReservations = {
    ...draft.installedReservations,
    blockQuantity: blockCount,
  };
  return applyGenerationProjectDutyBasis(source, draft);
}

/**
 * Apply one catalog-bound project dossier purely. Site capacities, receptors,
 * lighting, parcel controls and CCS mode remain untouched.
 */
export function applyGenerationProjectDutyBasis(
  source: GenerationImpactScenarioInput,
  draft: GenerationProjectDutyBasisDraft,
): GenerationImpactScenarioInput {
  const baselineSource = source.projectDutyBasis && source.equipmentBasis
    ? applyGenerationEquipmentEnvelope(
        source,
        source.equipmentBasis.catalogId,
        { allowUnconfirmed: true },
      )
    : source;
  const basis = finalizedBasis(baselineSource, draft);
  const applied = structuredClone(baselineSource);
  const blockCount = physicalBlockCount(applied);
  if (basis.installedReservations.blockQuantity !== blockCount) {
    throw new RangeError(
      `Installed reservation block quantity ${basis.installedReservations.blockQuantity} does not match the ${blockCount} physical generation blocks in the scenario.`,
    );
  }

  if (basis.performance) {
    const performance = basis.performance;
    applied.units = applied.units.map(unit => ({
      ...unit,
      ...(performance.referenceIsoGrossMWPerBlock == null
        ? {}
        : { isoGrossMWPerUnit: performance.referenceIsoGrossMWPerBlock }),
      ...(performance.ambientCorrection == null
        ? {}
        : { ambientFactor: performance.ambientCorrection.appliedGrossOutputFactor }),
      ...(performance.degradationFactor == null
        ? {}
        : { degradationFactor: performance.degradationFactor }),
      ...(performance.auxiliaryLoadMWPerBlock == null
        ? {}
        : { auxiliaryLoadMWPerUnit: performance.auxiliaryLoadMWPerBlock }),
      ...(performance.heatRateBtuPerKWhHhv == null
        ? {}
        : { heatRateBtuPerKWhHhv: performance.heatRateBtuPerKWhHhv }),
      ...(performance.forcedOutageRate == null
        ? {}
        : { forcedOutageRate: performance.forcedOutageRate }),
    }));
  }

  const acousticSources = acousticNoiseSources(basis);
  if (acousticSources) applied.noiseSources = acousticSources;

  if (basis.water?.kind === 'gross-output-intensity') {
    applied.water.generationWithdrawalGalPerMWhGross = basis.water.withdrawalGalPerMWhGross;
    applied.water.generationConsumptionGalPerMWhGross = basis.water.consumptionGalPerMWhGross;
    delete applied.water.generationDesignDay;
  } else if (basis.water?.kind === 'absolute-design-day') {
    applied.water.generationDesignDay = {
      withdrawalGalPerDay: basis.water.withdrawalGalPerDay,
      consumptionGalPerDay: basis.water.consumptionGalPerDay,
      referenceGrossMWhPerDay: basis.water.referenceGrossMWhPerDay,
    };
  }

  // Firm transport only — see `appliesFirmGasCapacity`. An indicative rating is
  // still recorded on the dossier and still derated; it simply never becomes
  // the scenario's firm capacity.
  if (appliesFirmGasCapacity(basis.fuelGas)) {
    applied.availableGasCapacityDthPerDay = firmGasDeliverableDthPerDay(basis.fuelGas!.supply!)!;
  }

  const installed = basis.installedReservations;
  applied.footprint.installedPlantAcres = installed.blockQuantity
    * areaAcres(installed.generationBlockEnvelope);
  if (installed.coolingEnvelope) {
    applied.footprint.coolingAcres = areaAcres(installed.coolingEnvelope);
  }
  if (installed.switchyardEnvelope) {
    applied.footprint.switchyardAcres = areaAcres(installed.switchyardEnvelope);
  }
  if (installed.ccsCaptureEnvelope) {
    applied.ccs.footprintAcres = areaAcres(installed.ccsCaptureEnvelope);
  }
  if (installed.ccsCompressionEnvelope) {
    applied.ccs.compressionFootprintAcres = areaAcres(installed.ccsCompressionEnvelope);
  }

  replaceProjectDutyAssumptions(applied, basis);
  applied.projectDutyBasis = basis;
  basis.appliedDutyFingerprint = hashGenerationProjectDutyApplication(applied, basis);
  basis.basisHash = hashGenerationProjectDutyBasis(basis);

  const errors = validateGenerationProjectDutyBasis(basis, applied);
  if (errors.length > 0) {
    throw new RangeError(`Project duty dossier is invalid: ${errors.join('; ')}`);
  }
  return applied;
}

function checkString(value: unknown, path: string, errors: string[]): void {
  if (!boundedString(value)) errors.push(`${path} must be a non-empty bounded string`);
}

function checkOptionalDate(value: unknown, path: string, errors: string[]): void {
  if (value != null && !dateString(value)) errors.push(`${path} must be YYYY-MM-DD`);
}

function validateSource(
  value: unknown,
  index: number,
  errors: string[],
): value is GenerationProjectDutySource {
  const path = `sources[${index}]`;
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return false;
  }
  if (!opaqueSourceId(value.id)) {
    errors.push(`${path}.id must be an opaque identifier using letters, numbers, hyphen or underscore`);
  }
  if (!['oem', 'epc', 'owner-engineer', 'utility', 'acoustic-consultant', 'water-provider', 'permit-study'].includes(String(value.authorityRole))) {
    errors.push(`${path}.authorityRole is unsupported`);
  }
  checkString(value.publisher, `${path}.publisher`, errors);
  checkString(value.title, `${path}.title`, errors);
  if (value.revision != null) checkString(value.revision, `${path}.revision`, errors);
  checkOptionalDate(value.issuedAt, `${path}.issuedAt`, errors);
  if (value.disclosure === 'public') {
    if (typeof value.url !== 'string' || !value.url.startsWith('https://')) {
      errors.push(`${path}.url must use HTTPS`);
    }
    if (!dateString(value.retrievedAt)) errors.push(`${path}.retrievedAt must be YYYY-MM-DD`);
    if ('fileName' in value || 'sha256' in value || 'siteDocumentId' in value) {
      errors.push(`${path} public source cannot contain private document fields`);
    }
  } else if (value.disclosure === 'private') {
    if (!boundedString(value.fileName)
      || value.fileName.includes('/')
      || value.fileName.includes('\\')) {
      errors.push(`${path}.fileName must be an inert bounded base name`);
    }
    if (typeof value.sha256 !== 'string' || !/^[a-f\d]{64}$/i.test(value.sha256)) {
      errors.push(`${path}.sha256 must be a full SHA-256 digest`);
    }
    if (value.siteDocumentId != null
      && (!boundedString(value.siteDocumentId)
        || value.siteDocumentId.includes('/')
        || value.siteDocumentId.includes('\\'))) {
      errors.push(`${path}.siteDocumentId must be a bounded opaque document identifier`);
    }
    if ('url' in value || 'retrievedAt' in value) {
      errors.push(`${path} private source cannot contain a URL`);
    }
  } else {
    errors.push(`${path}.disclosure is unsupported`);
  }
  return true;
}

function validateSourceIds(
  value: unknown,
  path: string,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  if (!Array.isArray(value)
    || value.length < 1
    || value.length > MAX_SOURCE_ID_COUNT
    || value.some(id => !opaqueSourceId(id))) {
    errors.push(`${path} must contain between 1 and ${MAX_SOURCE_ID_COUNT} opaque source ids`);
    return;
  }
  const ids = value as string[];
  if (!sortedUnique(ids)) errors.push(`${path} must be sorted and unique`);
  ids.forEach(id => {
    if (!knownSourceIds.has(id)) errors.push(`${path} references unknown source ${id}`);
  });
}

function validateEnvelope(
  value: unknown,
  path: string,
  errors: string[],
): value is GenerationProjectReservationEnvelope {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return false;
  }
  if (!finiteInRange(value.lengthM, 0.01, MAX_DIMENSION_M)) {
    errors.push(`${path}.lengthM must be finite and in (0, ${MAX_DIMENSION_M}]`);
  }
  if (!finiteInRange(value.widthM, 0.01, MAX_DIMENSION_M)) {
    errors.push(`${path}.widthM must be finite and in (0, ${MAX_DIMENSION_M}]`);
  }
  return true;
}

function validateNoiseDatum(value: unknown, path: string, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  if (!finiteInRange(value.levelDbA, -50, 250)) {
    errors.push(`${path}.levelDbA must be between -50 and 250 dBA`);
  }
  if (value.kind === 'sound-power') {
    if (value.directivityCorrectionDb != null
      && !finiteInRange(value.directivityCorrectionDb, -50, 50)) {
      errors.push(`${path}.directivityCorrectionDb must be between -50 and 50 dB`);
    }
  } else if (value.kind === 'reference-spl') {
    if (!finiteInRange(value.referenceDistanceM, 0.01, 100_000)) {
      errors.push(`${path}.referenceDistanceM must be in (0, 100000]`);
    }
    if (value.scope !== 'aggregate' && value.scope !== 'per-unit') {
      errors.push(`${path}.scope is unsupported`);
    }
    if (value.basisLabel != null && !boundedString(value.basisLabel)) {
      errors.push(`${path}.basisLabel must be a bounded string`);
    }
  } else {
    errors.push(`${path}.kind is unsupported`);
  }
}

function validatePerformance(
  value: unknown,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'performance';
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  validateSourceIds(value.sourceIds, `${path}.sourceIds`, knownSourceIds, errors);
  const overrideFields = [
    value.referenceIsoGrossMWPerBlock,
    value.ambientCorrection,
    value.degradationFactor,
    value.auxiliaryLoadMWPerBlock,
    value.heatRateBtuPerKWhHhv,
    value.forcedOutageRate,
  ];
  if (overrideFields.every(field => field == null)) {
    errors.push(`${path} must contain at least one applied performance override`);
  }
  if (value.referenceIsoGrossMWPerBlock != null
    && !finiteInRange(value.referenceIsoGrossMWPerBlock, 0.001, MAX_POWER_MW)) {
    errors.push(`${path}.referenceIsoGrossMWPerBlock must be finite and positive`);
  }
  if (value.degradationFactor != null
    && !finiteInRange(value.degradationFactor, 0.000_001, 1)) {
    errors.push(`${path}.degradationFactor must be in (0, 1]`);
  }
  if (value.auxiliaryLoadMWPerBlock != null
    && !finiteInRange(value.auxiliaryLoadMWPerBlock, 0, MAX_POWER_MW)) {
    errors.push(`${path}.auxiliaryLoadMWPerBlock must be finite and non-negative`);
  }
  if (value.heatRateBtuPerKWhHhv != null
    && !finiteInRange(value.heatRateBtuPerKWhHhv, 0.001, MAX_HEAT_RATE_BTU_PER_KWH)) {
    errors.push(`${path}.heatRateBtuPerKWhHhv must be finite and positive`);
  }
  if (value.forcedOutageRate != null
    && !finiteInRange(value.forcedOutageRate, 0, 1)) {
    errors.push(`${path}.forcedOutageRate must be in [0, 1]`);
  }
  if (!isRecord(value.referenceConditions)) {
    errors.push(`${path}.referenceConditions must be an object`);
  } else {
    const conditions = value.referenceConditions;
    if (conditions.elevationM != null && !finiteInRange(conditions.elevationM, -1_000, 20_000)) {
      errors.push(`${path}.referenceConditions.elevationM is outside the engineering range`);
    }
    if (conditions.relativeHumidityPct != null
      && !finiteInRange(conditions.relativeHumidityPct, 0, 100)) {
      errors.push(`${path}.referenceConditions.relativeHumidityPct must be in [0, 100]`);
    }
    if (conditions.barometricPressureKPaAbsolute != null
      && !finiteInRange(conditions.barometricPressureKPaAbsolute, 1, 200)) {
      errors.push(`${path}.referenceConditions.barometricPressureKPaAbsolute must be in [1, 200]`);
    }
    checkString(conditions.notes, `${path}.referenceConditions.notes`, errors);
  }
  checkString(value.normalizationNote, `${path}.normalizationNote`, errors);
  if (value.ambientCorrection != null) {
    if (!isRecord(value.ambientCorrection)) {
      errors.push(`${path}.ambientCorrection must be an object`);
    } else {
      const ambient = value.ambientCorrection;
      if (ambient.input !== 'dry-bulb-c'
        || ambient.output !== 'gross-output-factor'
        || ambient.interpolation !== 'piecewise-linear-no-extrapolation') {
        errors.push(`${path}.ambientCorrection contract is unsupported`);
      }
      if (!Array.isArray(ambient.points)) {
        errors.push(`${path}.ambientCorrection.points must be an array`);
      } else {
        try {
          const factor = interpolateAmbientOutputFactor(
            ambient.points as GenerationAmbientCorrectionPoint[],
            Number(ambient.designDryBulbC),
          );
          if (!finiteInRange(ambient.appliedGrossOutputFactor, 0.000_001, 1)
            || !nearlyEqual(ambient.appliedGrossOutputFactor, factor)) {
            errors.push(`${path}.ambientCorrection.appliedGrossOutputFactor does not match deterministic interpolation`);
          }
        } catch (error) {
          errors.push(`${path}.ambientCorrection ${String((error as Error).message)}`);
        }
      }
    }
  }
}

function validateInstalledReservations(
  value: unknown,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'installedReservations';
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  if (!finiteInRange(value.blockQuantity, 1, MAX_BLOCK_COUNT)
    || !Number.isInteger(value.blockQuantity)) {
    errors.push(`${path}.blockQuantity must be an integer in [1, ${MAX_BLOCK_COUNT}]`);
  }
  if (validateEnvelope(value.generationBlockEnvelope, `${path}.generationBlockEnvelope`, errors)) {
    const block = value.generationBlockEnvelope as unknown as Record<string, unknown>;
    if (block.scope !== 'installed-operating-envelope') {
      errors.push(`${path}.generationBlockEnvelope.scope is unsupported`);
    }
    if (typeof block.maintenanceClearanceIncluded !== 'boolean') {
      errors.push(`${path}.generationBlockEnvelope.maintenanceClearanceIncluded must be boolean`);
    }
    if (typeof block.fireAccessIncluded !== 'boolean') {
      errors.push(`${path}.generationBlockEnvelope.fireAccessIncluded must be boolean`);
    }
  }
  [
    'coolingEnvelope',
    'switchyardEnvelope',
    'ccsCaptureEnvelope',
    'ccsCompressionEnvelope',
    'naturalGasRouteEnvelope',
    'co2ExportRouteEnvelope',
    'constructionLaydownEnvelope',
  ].forEach(field => {
    if (value[field] != null) validateEnvelope(value[field], `${path}.${field}`, errors);
  });
  validateSourceIds(value.sourceIds, `${path}.sourceIds`, knownSourceIds, errors);
}

function validateFuelGas(
  value: unknown,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'fuelGas';
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  const pressure = (datum: unknown, datumPath: string): void => {
    if (!isRecord(datum)) {
      errors.push(`${datumPath} must be an object`);
      return;
    }
    if (!finiteInRange(datum.valueKPa, 0, MAX_PRESSURE_KPA)) {
      errors.push(`${datumPath}.valueKPa must be finite and non-negative`);
    }
    if (datum.reference !== 'gauge' && datum.reference !== 'absolute') {
      errors.push(`${datumPath}.reference is unsupported`);
    }
  };
  pressure(value.minimumRequired, `${path}.minimumRequired`);
  if (value.maximumAllowed != null) pressure(value.maximumAllowed, `${path}.maximumAllowed`);
  if (value.documentedSiteSupplyMinimum != null) {
    pressure(value.documentedSiteSupplyMinimum, `${path}.documentedSiteSupplyMinimum`);
  }
  if (value.documentedSiteSupplyMaximum != null) {
    pressure(value.documentedSiteSupplyMaximum, `${path}.documentedSiteSupplyMaximum`);
  }
  if (isRecord(value.documentedSiteSupplyMinimum) && isRecord(value.documentedSiteSupplyMaximum)
    && value.documentedSiteSupplyMinimum.reference === value.documentedSiteSupplyMaximum.reference
    && typeof value.documentedSiteSupplyMinimum.valueKPa === 'number'
    && typeof value.documentedSiteSupplyMaximum.valueKPa === 'number'
    && value.documentedSiteSupplyMinimum.valueKPa > value.documentedSiteSupplyMaximum.valueKPa) {
    errors.push(`${path}.documentedSiteSupplyMinimum cannot exceed documentedSiteSupplyMaximum on the same pressure reference`);
  }
  if (isRecord(value.minimumRequired) && isRecord(value.maximumAllowed)
    && value.minimumRequired.reference === value.maximumAllowed.reference
    && typeof value.minimumRequired.valueKPa === 'number'
    && typeof value.maximumAllowed.valueKPa === 'number'
    && value.minimumRequired.valueKPa > value.maximumAllowed.valueKPa) {
    errors.push(`${path}.minimumRequired cannot exceed maximumAllowed on the same pressure reference`);
  }
  if (value.supply != null) {
    validateFuelGasSupply(value.supply, pressure, knownSourceIds, errors);
  }
  validateSourceIds(value.sourceIds, `${path}.sourceIds`, knownSourceIds, errors);
}

/**
 * All-or-nothing. The pressure-derate screen is not computable from a subset,
 * so a partially-filled supply record must not be representable — a half-filled
 * one would otherwise reach `basisHash` and the solver unvalidated
 * (`validateFuelGas` has no exact-key check).
 */
function validateFuelGasSupply(
  value: unknown,
  pressure: (datum: unknown, datumPath: string) => void,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'fuelGas.supply';
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  if (value.quantityBasis !== 'firm-transport-agreement' && value.quantityBasis !== 'indicative') {
    errors.push(`${path}.quantityBasis is unsupported`);
  }
  // Single-member union: absolute-pressure scaling holds only for a
  // velocity-limited meter run. A regulation- or compression-limited station is
  // rejected rather than silently screened with the wrong physics.
  if (value.capacityLimit !== 'velocity-limited-meter-run'
    && value.capacityLimit !== 'documented-derated-at-site-minimum') {
    errors.push(`${path}.capacityLimit is unsupported; use 'velocity-limited-meter-run' for a meter-run-limited station, or 'documented-derated-at-site-minimum' when the quoted figure is already stated at the site's minimum delivery pressure`);
  }
  const basis = value.capacityBasis;
  if (!isRecord(basis)) {
    errors.push(`${path}.capacityBasis must be an object`);
  } else if (basis.kind === 'standard-volume') {
    if (!finiteInRange(basis.documentedHhvBtuPerScf, 1, MAX_HHV_BTU_PER_SCF)) {
      errors.push(`${path}.capacityBasis.documentedHhvBtuPerScf must be a documented heating value`);
    }
    const conditions = basis.standardConditions;
    if (!isRecord(conditions)) {
      errors.push(`${path}.capacityBasis.standardConditions must be an object`);
    } else {
      if (!finiteInRange(conditions.basePressureKPaAbsolute, 1, MAX_PRESSURE_KPA)) {
        errors.push(`${path}.capacityBasis.standardConditions.basePressureKPaAbsolute must be finite and positive`);
      }
      if (!finiteInRange(conditions.baseTemperatureC, -273.15, 1_000)) {
        errors.push(`${path}.capacityBasis.standardConditions.baseTemperatureC must be a physical temperature`);
      }
    }
  } else if (basis.kind !== 'energy') {
    errors.push(`${path}.capacityBasis.kind is unsupported`);
  }
  // Bound is in Dth/day; a volumetric rating is orders of magnitude smaller, so
  // this is a sanity ceiling on both bases rather than a unit assertion.
  if (!finiteInRange(value.stationDesignCapacity, 0, MAX_GAS_CAPACITY_DTH_PER_DAY)) {
    errors.push(`${path}.stationDesignCapacity must be finite and non-negative`);
  }
  if (value.contractMaxDailyQuantity != null
    && !finiteInRange(value.contractMaxDailyQuantity, 0, MAX_GAS_CAPACITY_DTH_PER_DAY)) {
    errors.push(`${path}.contractMaxDailyQuantity must be finite and non-negative`);
  }
  pressure(value.ratedAtPressure, `${path}.ratedAtPressure`);
  const compressibility = value.compressibility;
  if (compressibility != null) {
    if (!isRecord(compressibility)) {
      errors.push(`${path}.compressibility must be an object`);
    } else {
      (['atRatedPressure', 'atMinimumDeliveryPressure'] as const).forEach(key => {
        if (!finiteInRange(compressibility[key], 0.1, MAX_COMPRESSIBILITY)) {
          errors.push(`${path}.compressibility.${key} must be a physical compressibility factor`);
        }
      });
    }
  }
  checkString(value.operatingCase, `${path}.operatingCase`, errors);
  validateSourceIds(value.sourceIds, `${path}.sourceIds`, knownSourceIds, errors);
}

function validateAcoustics(
  value: unknown,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'acoustics';
  if (!isRecord(value)
    || !Array.isArray(value.acousticSources)
    || value.acousticSources.length < 1
    || value.acousticSources.length > MAX_ACOUSTIC_SOURCE_COUNT) {
    errors.push(`${path}.acousticSources must contain between 1 and ${MAX_ACOUSTIC_SOURCE_COUNT} sources`);
    return;
  }
  const ids: string[] = [];
  value.acousticSources.forEach((source, index) => {
    const sourcePath = `${path}.acousticSources[${index}]`;
    if (!isRecord(source)) {
      errors.push(`${sourcePath} must be an object`);
      return;
    }
    if (!opaqueSourceId(source.id)) {
      errors.push(`${sourcePath}.id must be an opaque identifier using letters, numbers, hyphen or underscore`);
    }
    if (typeof source.id === 'string') ids.push(source.id);
    checkString(source.label, `${sourcePath}.label`, errors);
    checkString(source.operatingCase, `${sourcePath}.operatingCase`, errors);
    if (!isRecord(source.quantity)) {
      errors.push(`${sourcePath}.quantity must be an object`);
    } else {
      if (source.quantity.scope !== 'site-total'
        && source.quantity.scope !== 'per-generation-block') {
        errors.push(`${sourcePath}.quantity.scope is unsupported`);
      }
      if (!finiteInRange(source.quantity.count, 1, 1_000_000)
        || !Number.isInteger(source.quantity.count)) {
        errors.push(`${sourcePath}.quantity.count must be a positive bounded integer`);
      }
    }
    validateNoiseDatum(source.appliedDatum, `${sourcePath}.appliedDatum`, errors);
    validateSourceIds(source.sourceIds, `${sourcePath}.sourceIds`, knownSourceIds, errors);
    if (isRecord(source.appliedDatum) && source.appliedDatum.kind === 'reference-spl') {
      const expectedScope = isRecord(source.quantity)
        && source.quantity.scope === 'site-total' ? 'aggregate' : 'per-unit';
      if (source.appliedDatum.scope !== expectedScope) {
        errors.push(`${sourcePath}.appliedDatum.scope must be ${expectedScope} for its quantity scope`);
      }
    }
  });
  if (ids.length !== new Set(ids).size) errors.push(`${path}.acousticSources ids must be unique`);
}

function validateWater(
  value: unknown,
  knownSourceIds: ReadonlySet<string>,
  errors: string[],
): void {
  const path = 'water';
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  checkString(value.operatingCase, `${path}.operatingCase`, errors);
  validateSourceIds(value.sourceIds, `${path}.sourceIds`, knownSourceIds, errors);
  if (value.kind === 'gross-output-intensity') {
    if (!finiteInRange(value.withdrawalGalPerMWhGross, 0, MAX_DAILY_WATER_GAL)) {
      errors.push(`${path}.withdrawalGalPerMWhGross must be finite and non-negative`);
    }
    if (!finiteInRange(value.consumptionGalPerMWhGross, 0, MAX_DAILY_WATER_GAL)) {
      errors.push(`${path}.consumptionGalPerMWhGross must be finite and non-negative`);
    }
    if (typeof value.withdrawalGalPerMWhGross === 'number'
      && typeof value.consumptionGalPerMWhGross === 'number'
      && value.withdrawalGalPerMWhGross < value.consumptionGalPerMWhGross) {
      errors.push(`${path} withdrawal cannot be below consumption`);
    }
  } else if (value.kind === 'absolute-design-day') {
    if (!finiteInRange(value.withdrawalGalPerDay, 0, MAX_DAILY_WATER_GAL)) {
      errors.push(`${path}.withdrawalGalPerDay must be finite and non-negative`);
    }
    if (!finiteInRange(value.consumptionGalPerDay, 0, MAX_DAILY_WATER_GAL)) {
      errors.push(`${path}.consumptionGalPerDay must be finite and non-negative`);
    }
    if (!finiteInRange(value.referenceGrossMWhPerDay, 0.001, MAX_DAILY_GROSS_MWH)) {
      errors.push(`${path}.referenceGrossMWhPerDay must be finite and positive, at most ${MAX_DAILY_GROSS_MWH} MWh/day`);
    }
    if (typeof value.withdrawalGalPerDay === 'number'
      && typeof value.consumptionGalPerDay === 'number'
      && value.withdrawalGalPerDay < value.consumptionGalPerDay) {
      errors.push(`${path} withdrawal cannot be below consumption`);
    }
  } else {
    errors.push(`${path}.kind is unsupported`);
  }
}

/** Runtime authority guard shared by server, scenario and exhibit boundaries. */
export function validateGenerationProjectDutyBasis(
  value: unknown,
  scenario?: GenerationImpactScenarioInput,
): readonly string[] {
  if (!isRecord(value)) return ['project duty basis must be an object'];
  const errors: string[] = [];
  if (value.schemaVersion !== GENERATION_PROJECT_DUTY_SCHEMA_VERSION) {
    errors.push('schemaVersion is unsupported');
  }
  if (value.modelVersion !== GENERATION_PROJECT_DUTY_MODEL_VERSION) {
    errors.push('modelVersion is unsupported');
  }
  if (value.hashVersion !== GENERATION_PROJECT_DUTY_HASH_VERSION) {
    errors.push('hashVersion is unsupported');
  }
  checkString(value.basisId, 'basisId', errors);
  checkString(value.basisName, 'basisName', errors);
  // EXACTLY ONE binding. The two are different provenance classes — an
  // immutable catalog row and an unverified user entry — and a dossier claiming
  // both would make its restoration target ambiguous.
  const hasCatalogBinding = isRecord(value.catalogBinding);
  const hasUserBinding = isRecord(value.userEquipmentBinding);
  if (hasCatalogBinding === hasUserBinding) {
    errors.push('project duty basis requires exactly one of catalogBinding or userEquipmentBinding');
  }
  if (isRecord(value.catalogBinding)) {
    checkString(value.catalogBinding.catalogId, 'catalogBinding.catalogId', errors);
    if (typeof value.catalogBinding.catalogAppliedDutyFingerprint !== 'string'
      || !/^[a-f\d]{64}$/i.test(value.catalogBinding.catalogAppliedDutyFingerprint)) {
      errors.push('catalogBinding.catalogAppliedDutyFingerprint must be a SHA-256 digest');
    }
  }
  if (isRecord(value.userEquipmentBinding)) {
    checkString(value.userEquipmentBinding.entryId, 'userEquipmentBinding.entryId', errors);
    if (typeof value.userEquipmentBinding.appliedDutyFingerprint !== 'string'
      || !/^[a-f\d]{64}$/i.test(value.userEquipmentBinding.appliedDutyFingerprint)) {
      errors.push('userEquipmentBinding.appliedDutyFingerprint must be a SHA-256 digest');
    }
  }
  if (!isRecord(value.restorationBaseline)) {
    errors.push('restorationBaseline must be an object');
  } else {
    if (!finiteInRange(
      value.restorationBaseline.ccsCaptureFootprintAcres,
      0,
      MAX_DIMENSION_M * MAX_DIMENSION_M / SQUARE_METRES_PER_ACRE,
    )) {
      errors.push('restorationBaseline.ccsCaptureFootprintAcres must be finite and non-negative');
    }
    if (!finiteInRange(
      value.restorationBaseline.ccsCompressionFootprintAcres,
      0,
      MAX_DIMENSION_M * MAX_DIMENSION_M / SQUARE_METRES_PER_ACRE,
    )) {
      errors.push('restorationBaseline.ccsCompressionFootprintAcres must be finite and non-negative');
    }
  }
  if (!Array.isArray(value.sources)
    || value.sources.length < 1
    || value.sources.length > MAX_SOURCE_COUNT) {
    errors.push(`sources must contain between 1 and ${MAX_SOURCE_COUNT} records`);
  }
  const sources = Array.isArray(value.sources) ? value.sources : [];
  sources.forEach((source, index) => validateSource(source, index, errors));
  const sourceIds = sources.flatMap(source => (
    isRecord(source) && typeof source.id === 'string' ? [source.id] : []
  ));
  if (sourceIds.length !== new Set(sourceIds).size) errors.push('sources ids must be unique');
  if (!sortedUnique(sourceIds)) errors.push('sources must be sorted by id');
  const knownSourceIds = new Set(sourceIds);

  if (value.performance != null) validatePerformance(value.performance, knownSourceIds, errors);
  validateInstalledReservations(value.installedReservations, knownSourceIds, errors);
  if (value.fuelGas != null) validateFuelGas(value.fuelGas, knownSourceIds, errors);
  if (value.acoustics != null) validateAcoustics(value.acoustics, knownSourceIds, errors);
  if (value.water != null) validateWater(value.water, knownSourceIds, errors);

  if (typeof value.basisHash !== 'string' || !/^[a-f\d]{64}$/i.test(value.basisHash)) {
    errors.push('basisHash must be a SHA-256 digest');
  }
  if (typeof value.appliedDutyFingerprint !== 'string'
    || !/^[a-f\d]{64}$/i.test(value.appliedDutyFingerprint)) {
    errors.push('appliedDutyFingerprint must be a SHA-256 digest');
  }
  if (errors.length === 0) {
    try {
      if (hashGenerationProjectDutyBasis(value as unknown as GenerationProjectDutyBasis)
        !== value.basisHash) {
        errors.push('basisHash does not match the canonical project duty dossier');
      }
    } catch (error) {
      errors.push(`project duty dossier cannot be hashed: ${(error as Error).message}`);
    }
  }

  if (scenario) {
    const equipment = scenario.equipmentBasis;
    const userBinding = equipment ? null : resolveUserEquipmentBinding(scenario);
    if (!equipment && !userBinding) {
      errors.push('project duty basis requires an applied equipment basis');
    } else if (userBinding) {
      if (!isRecord(value.userEquipmentBinding)) {
        errors.push('project duty basis bound to a catalog envelope cannot apply to a user-supplied duty');
      } else {
        if (value.userEquipmentBinding.entryId !== userBinding.entryId) {
          errors.push('userEquipmentBinding.entryId does not match the applied user equipment');
        }
        if (value.userEquipmentBinding.appliedDutyFingerprint !== userBinding.appliedDutyFingerprint) {
          errors.push('userEquipmentBinding fingerprint does not match the applied user equipment duty');
        }
      }
    } else if (equipment && isRecord(value.catalogBinding)) {
      if (value.catalogBinding.catalogId !== equipment.catalogId) {
        errors.push('catalogBinding.catalogId does not match the selected equipment basis');
      }
      if (value.catalogBinding.catalogAppliedDutyFingerprint
        !== equipment.appliedDutyFingerprint) {
        errors.push('catalogBinding fingerprint does not match the selected equipment basis');
      }
    } else if (equipment && isRecord(value.userEquipmentBinding)) {
      errors.push('project duty basis bound to a user entry cannot apply to a catalog envelope');
    }
    if (isRecord(value.installedReservations)
      && value.installedReservations.blockQuantity !== physicalBlockCount(scenario)) {
      errors.push('installed reservation block quantity does not match the scenario fleet');
    }
  }
  return errors;
}

function expectedProjectNoiseSources(
  basis: GenerationProjectDutyBasis,
): GenerationNoiseUnit[] | null {
  return acousticNoiseSources(basis) ?? null;
}

/** Explain whether equipment-linked scenario fields still match the applied dossier. */
export function evaluateGenerationProjectDutyAlignment(
  input: GenerationImpactScenarioInput,
): Omit<GenerationProjectDutyExhibitBasis, 'basis' | 'completeness' | 'gasPressureCheck' | 'spatialSummary'> | null {
  const basis = input.projectDutyBasis;
  if (!basis) return null;
  const currentDutyFingerprint = hashGenerationProjectDutyApplication(input);
  if (currentDutyFingerprint === basis.appliedDutyFingerprint) {
    return { currentDutyFingerprint, alignmentStatus: 'aligned', alignmentNotes: [] };
  }
  const notes: string[] = [];
  if (physicalBlockCount(input) !== basis.installedReservations.blockQuantity) {
    notes.push('Physical generation block count differs from the installed project reservation.');
  }
  const performance = basis.performance;
  if (performance && input.units.some(unit => (
    (performance.referenceIsoGrossMWPerBlock != null
      && !nearlyEqual(unit.isoGrossMWPerUnit, performance.referenceIsoGrossMWPerBlock))
    || (performance.ambientCorrection != null
      && !nearlyEqual(unit.ambientFactor, performance.ambientCorrection.appliedGrossOutputFactor))
    || (performance.degradationFactor != null
      && !nearlyEqual(unit.degradationFactor, performance.degradationFactor))
    || (performance.auxiliaryLoadMWPerBlock != null
      && !nearlyEqual(unit.auxiliaryLoadMWPerUnit, performance.auxiliaryLoadMWPerBlock))
    || (performance.heatRateBtuPerKWhHhv != null
      && !nearlyEqual(unit.heatRateBtuPerKWhHhv, performance.heatRateBtuPerKWhHhv))
    || (performance.forcedOutageRate != null
      && !nearlyEqual(unit.forcedOutageRate ?? Number.NaN, performance.forcedOutageRate))
  ))) {
    notes.push('Power-block rating, ambient correction, degradation, auxiliary, heat-rate, or outage inputs differ from the project dossier.');
  }
  const expectedPlantAcres = basis.installedReservations.blockQuantity
    * areaAcres(basis.installedReservations.generationBlockEnvelope);
  if (!nearlyEqual(input.footprint.installedPlantAcres ?? Number.NaN, expectedPlantAcres)) {
    notes.push('Absolute installed plant area differs from the project block envelopes.');
  }
  if (basis.water?.kind === 'gross-output-intensity' && (
    !nearlyEqual(input.water.generationWithdrawalGalPerMWhGross, basis.water.withdrawalGalPerMWhGross)
    || !nearlyEqual(input.water.generationConsumptionGalPerMWhGross, basis.water.consumptionGalPerMWhGross)
    || input.water.generationDesignDay != null
  )) {
    notes.push('Generation water intensity differs from the project water balance.');
  }
  if (basis.water?.kind === 'absolute-design-day' && canonicalJson(
    input.water.generationDesignDay ?? null,
  ) !== canonicalJson({
    withdrawalGalPerDay: basis.water.withdrawalGalPerDay,
    consumptionGalPerDay: basis.water.consumptionGalPerDay,
    referenceGrossMWhPerDay: basis.water.referenceGrossMWhPerDay,
  })) {
    notes.push('Absolute generation design-day water duty differs from the project water balance.');
  }
  if (canonicalJson(input.noiseSources ?? null)
    !== canonicalJson(expectedProjectNoiseSources(basis))) {
    notes.push('Applied broadband acoustic sources differ from the project dossier.');
  }
  if (appliesFirmGasCapacity(basis.fuelGas) && !nearlyEqual(
    input.availableGasCapacityDthPerDay ?? Number.NaN,
    firmGasDeliverableDthPerDay(basis.fuelGas!.supply!)!,
  )) {
    notes.push('Firm gas delivery capacity differs from the project interconnect duty.');
  }
  if (notes.length === 0) {
    notes.push('One or more project-duty-linked scenario fields changed after application.');
  }
  return { currentDutyFingerprint, alignmentStatus: 'modified', alignmentNotes: notes };
}

export function generationProjectDutyCompleteness(
  basis: GenerationProjectDutyBasis,
  ccsEnabled = true,
): GenerationProjectDutyCompleteness {
  const performance = basis.performance;
  const ambientPerformancePresent = Boolean(
    performance?.ambientCorrection
    || performance?.referenceIsoGrossMWPerBlock != null
    || performance?.degradationFactor != null
    || performance?.auxiliaryLoadMWPerBlock != null
    || performance?.heatRateBtuPerKWhHhv != null,
  );
  const performanceComplete = Boolean(
    performance?.ambientCorrection
    && performance.referenceIsoGrossMWPerBlock != null
    && performance.degradationFactor != null
    && performance.auxiliaryLoadMWPerBlock != null
    && performance.heatRateBtuPerKWhHhv != null,
  );
  const installed = basis.installedReservations;
  const ccsParts = [
    installed.ccsCaptureEnvelope,
    installed.ccsCompressionEnvelope,
    installed.co2ExportRouteEnvelope,
  ].filter(Boolean).length;
  const statuses: GenerationProjectDutyCompleteness['statuses'] = {
    ambientPerformance: performanceComplete
      ? 'supplied'
      : ambientPerformancePresent ? 'partial' : 'missing',
    fuelGasPressure: basis.fuelGas?.documentedSiteSupplyMinimum
      ? 'supplied'
      : basis.fuelGas ? 'partial' : 'missing',
    acousticSource: basis.acoustics?.acousticSources.length ? 'supplied' : 'missing',
    waterBalance: basis.water ? 'supplied' : 'missing',
    installedPlant: installed.generationBlockEnvelope.maintenanceClearanceIncluded
      && installed.generationBlockEnvelope.fireAccessIncluded
      && installed.coolingEnvelope != null
      && installed.switchyardEnvelope != null
      ? 'supplied'
      : 'partial',
    naturalGasRoute: installed.naturalGasRouteEnvelope ? 'supplied' : 'missing',
    ccsIntegration: !ccsEnabled
      ? 'not-applicable'
      : ccsParts === 3 ? 'supplied' : ccsParts > 0 ? 'partial' : 'missing',
    reliability: performance?.forcedOutageRate != null ? 'supplied' : 'missing',
  };
  const labels: Record<keyof typeof statuses, string> = {
    ambientPerformance: 'complete ambient performance duty',
    fuelGasPressure: 'fuel-gas requirement and documented site pressure',
    acousticSource: 'applied broadband acoustic source data',
    waterBalance: 'project generation water balance',
    installedPlant: 'installed block envelope including maintenance clearance and fire access',
    naturalGasRoute: 'natural-gas route reservation',
    ccsIntegration: 'CCS capture, compression, and CO₂ export route reservations',
    reliability: 'contractual outage or availability basis',
  };
  const entries = Object.entries(statuses) as Array<[
    keyof typeof statuses,
    GenerationProjectDutyCompleteness['statuses'][keyof typeof statuses],
  ]>;
  return {
    statuses,
    suppliedCount: entries.filter(([, status]) => status === 'supplied').length,
    totalCount: entries.filter(([, status]) => status !== 'not-applicable').length,
    missingItems: entries
      .filter(([, status]) => status !== 'supplied' && status !== 'not-applicable')
      .map(([key, status]) => `${labels[key]} (${status})`),
  };
}

export function generationProjectDutyGasPressureCheck(
  basis: GenerationProjectDutyBasis,
): GenerationProjectDutyExhibitBasis['gasPressureCheck'] {
  const gas = basis.fuelGas;
  const supply = gas?.documentedSiteSupplyMinimum;
  const hydraulicCaveat = 'Static pressure-window screen only; confirm peak-flow pressure drop, regulation, compression, gas composition and transient delivery with the utility and OEM.';
  // A documented site MAXIMUM alone is enough to screen the upper bound, so an
  // over-pressure is caught even when the transporter has not stated a minimum.
  // No pre-existing dossier carries a site maximum, so this still short-circuits
  // identically for every persisted dossier.
  if (!gas || (!supply && !gas.documentedSiteSupplyMaximum)) {
    return {
      verdict: 'indeterminate',
      marginKPa: null,
      upperMarginKPa: null,
      caveat: 'Document both equipment minimum inlet pressure and the site delivery minimum before assessing pressure adequacy.',
    };
  }
  const minimumComparable = supply != null
    && supply.reference === gas.minimumRequired.reference;
  const maximum = gas.maximumAllowed;
  // The upper bound is measured from the documented site MAXIMUM when one
  // exists. With none documented this falls back to the site minimum, which is
  // byte-for-byte the pre-existing behaviour (and why the caveat below still
  // disclaims that it cannot establish the site's actual maximum).
  const siteMaximum = gas.documentedSiteSupplyMaximum;
  // Non-null: the early return above guarantees at least one of the two exists.
  const upperReference = (siteMaximum ?? supply)!;
  const maximumComparable = maximum == null
    ? null
    : upperReference.reference === maximum.reference;
  const marginKPa = minimumComparable
    ? supply.valueKPa - gas.minimumRequired.valueKPa
    : null;
  const upperMarginKPa = maximumComparable
    ? maximum!.valueKPa - upperReference.valueKPa
    : null;
  const belowMinimum = marginKPa != null && marginKPa < 0;
  const aboveMaximum = upperMarginKPa != null && upperMarginKPa < 0;
  const verdict: EngineeringVerdict = belowMinimum || aboveMaximum
    ? 'fail'
    : !minimumComparable || maximumComparable === false
      ? 'indeterminate'
      : 'pass';
  const findings = [
    ...(supply == null
      ? ['Site delivery minimum is not documented; the minimum bound is not screened.']
      : !minimumComparable
        ? ['Equipment minimum and site supply use different pressure references; convert them to a common basis.']
        : belowMinimum
          ? [`Documented site minimum is ${Math.abs(marginKPa!)} kPa below the equipment minimum.`]
          : []),
    // Every `siteMaximum == null` branch reproduces the original string exactly;
    // frozen exhibits re-derive this object and byte-compare it.
    ...(maximum == null
      ? ['Equipment maximum inlet pressure is not supplied; the verdict screens the minimum bound only.']
      : maximumComparable === false
        ? [siteMaximum
            ? 'Equipment maximum and documented site maximum use different pressure references; convert them to a common basis.'
            : 'Equipment maximum and site supply use different pressure references; convert them to a common basis.']
        : aboveMaximum
          ? [siteMaximum
              ? `Documented site maximum is ${Math.abs(upperMarginKPa!)} kPa above the equipment maximum; the delivery point can over-pressure the equipment inlet.`
              : `Documented site minimum is ${Math.abs(upperMarginKPa!)} kPa above the equipment maximum.`]
          : [siteMaximum
              ? `Upper margin is measured from the documented site maximum; ${Math.abs(upperMarginKPa!)} kPa of headroom remains at the equipment inlet.`
              : 'Upper screening headroom is measured from the documented site minimum; it does not establish the actual site maximum pressure.']),
  ];
  return {
    verdict,
    marginKPa,
    upperMarginKPa,
    caveat: [...findings, hydraulicCaveat].join(' '),
  };
}

/** Deterministic report projection of the project OEM/EPC authority. */
export function projectGenerationProjectDutyExhibitBasis(
  input: GenerationImpactScenarioInput,
): GenerationProjectDutyExhibitBasis | undefined {
  const basis = input.projectDutyBasis;
  const alignment = evaluateGenerationProjectDutyAlignment(input);
  if (!basis || !alignment) return undefined;
  return {
    basis: structuredClone(basis),
    ...alignment,
    completeness: generationProjectDutyCompleteness(basis, input.ccs.enabled),
    gasPressureCheck: generationProjectDutyGasPressureCheck(basis),
    spatialSummary: generationProjectDutySpatialSummary(
      basis.installedReservations,
      input.ccs.enabled,
    ),
  };
}

function publicEnvelope(
  envelope: GenerationProjectReservationEnvelope,
): GenerationProjectReservationEnvelope {
  return { lengthM: envelope.lengthM, widthM: envelope.widthM };
}

function publicPerformance(
  basis: GenerationProjectDutyBasis,
): PublicGenerationProjectDutyExhibitBasis['performance'] | undefined {
  const performance = basis.performance;
  if (!performance) return undefined;
  return {
    ...(performance.referenceIsoGrossMWPerBlock == null
      ? {} : { referenceIsoGrossMWPerBlock: performance.referenceIsoGrossMWPerBlock }),
    ...(performance.ambientCorrection == null ? {} : {
      ambientCorrection: {
        input: performance.ambientCorrection.input,
        output: performance.ambientCorrection.output,
        interpolation: performance.ambientCorrection.interpolation,
        designDryBulbC: performance.ambientCorrection.designDryBulbC,
        points: performance.ambientCorrection.points.map(point => ({
          dryBulbC: point.dryBulbC,
          grossOutputFactor: point.grossOutputFactor,
        })),
        appliedGrossOutputFactor:
          performance.ambientCorrection.appliedGrossOutputFactor,
      },
    }),
    ...(performance.degradationFactor == null
      ? {} : { degradationFactor: performance.degradationFactor }),
    ...(performance.auxiliaryLoadMWPerBlock == null
      ? {} : { auxiliaryLoadMWPerBlock: performance.auxiliaryLoadMWPerBlock }),
    ...(performance.heatRateBtuPerKWhHhv == null
      ? {} : { heatRateBtuPerKWhHhv: performance.heatRateBtuPerKWhHhv }),
    ...(performance.forcedOutageRate == null
      ? {} : { forcedOutageRate: performance.forcedOutageRate }),
    referenceConditions: {
      ...(performance.referenceConditions.elevationM == null
        ? {} : { elevationM: performance.referenceConditions.elevationM }),
      ...(performance.referenceConditions.relativeHumidityPct == null
        ? {} : { relativeHumidityPct: performance.referenceConditions.relativeHumidityPct }),
      ...(performance.referenceConditions.barometricPressureKPaAbsolute == null
        ? {} : {
            barometricPressureKPaAbsolute:
              performance.referenceConditions.barometricPressureKPaAbsolute,
          }),
      notes: performance.referenceConditions.notes,
    },
    normalizationNote: performance.normalizationNote,
  };
}

function publicInstalledReservations(
  basis: GenerationProjectDutyBasis,
): PublicGenerationProjectDutyExhibitBasis['installedReservations'] {
  const installed = basis.installedReservations;
  return {
    blockQuantity: installed.blockQuantity,
    generationBlockEnvelope: {
      ...publicEnvelope(installed.generationBlockEnvelope),
      scope: installed.generationBlockEnvelope.scope,
      maintenanceClearanceIncluded:
        installed.generationBlockEnvelope.maintenanceClearanceIncluded,
      fireAccessIncluded: installed.generationBlockEnvelope.fireAccessIncluded,
    },
    ...(installed.coolingEnvelope
      ? { coolingEnvelope: publicEnvelope(installed.coolingEnvelope) } : {}),
    ...(installed.switchyardEnvelope
      ? { switchyardEnvelope: publicEnvelope(installed.switchyardEnvelope) } : {}),
    ...(installed.ccsCaptureEnvelope
      ? { ccsCaptureEnvelope: publicEnvelope(installed.ccsCaptureEnvelope) } : {}),
    ...(installed.ccsCompressionEnvelope
      ? { ccsCompressionEnvelope: publicEnvelope(installed.ccsCompressionEnvelope) } : {}),
    ...(installed.naturalGasRouteEnvelope
      ? { naturalGasRouteEnvelope: publicEnvelope(installed.naturalGasRouteEnvelope) } : {}),
    ...(installed.co2ExportRouteEnvelope
      ? { co2ExportRouteEnvelope: publicEnvelope(installed.co2ExportRouteEnvelope) } : {}),
    ...(installed.constructionLaydownEnvelope
      ? { constructionLaydownEnvelope: publicEnvelope(installed.constructionLaydownEnvelope) } : {}),
  };
}

function publicNoiseDatum(datum: NoiseDatum): NoiseDatum {
  return datum.kind === 'sound-power'
    ? {
        kind: 'sound-power',
        levelDbA: datum.levelDbA,
        ...(datum.directivityCorrectionDb == null
          ? {} : { directivityCorrectionDb: datum.directivityCorrectionDb }),
      }
    : {
        kind: 'reference-spl',
        levelDbA: datum.levelDbA,
        referenceDistanceM: datum.referenceDistanceM,
        scope: datum.scope,
        ...(datum.basisLabel == null ? {} : { basisLabel: datum.basisLabel }),
      };
}

/**
 * Explicit exact-key public projection. No private source locator, internal
 * source id, restoration metadata, or unvalidated extension key can cross.
 */
export function projectPublicGenerationProjectDutyExhibitBasis(
  projection: GenerationProjectDutyExhibitBasis,
): PublicGenerationProjectDutyExhibitBasis {
  const { basis } = projection;
  const performance = publicPerformance(basis);
  const fuelGas: PublicGenerationProjectDutyExhibitBasis['fuelGas'] = basis.fuelGas
    ? {
        minimumRequired: { ...basis.fuelGas.minimumRequired },
        ...(basis.fuelGas.maximumAllowed
          ? { maximumAllowed: { ...basis.fuelGas.maximumAllowed } } : {}),
        ...(basis.fuelGas.documentedSiteSupplyMinimum
          ? { documentedSiteSupplyMinimum: { ...basis.fuelGas.documentedSiteSupplyMinimum } } : {}),
        ...(basis.fuelGas.documentedSiteSupplyMaximum
          ? { documentedSiteSupplyMaximum: { ...basis.fuelGas.documentedSiteSupplyMaximum } } : {}),
        // Exact-key allowlist, and `sourceIds` is stripped one level deeper.
        // `Omit` is shallow, so the public type cannot enforce this on its own —
        // `projectPublicGenerationProjectDutyExhibitBasis.test` is the guard.
        ...(basis.fuelGas.supply ? {
          supply: {
            quantityBasis: basis.fuelGas.supply.quantityBasis,
            capacityBasis: basis.fuelGas.supply.capacityBasis.kind === 'standard-volume'
              ? {
                  kind: 'standard-volume' as const,
                  documentedHhvBtuPerScf: basis.fuelGas.supply.capacityBasis.documentedHhvBtuPerScf,
                  standardConditions: { ...basis.fuelGas.supply.capacityBasis.standardConditions },
                }
              : { kind: 'energy' as const },
            stationDesignCapacity: basis.fuelGas.supply.stationDesignCapacity,
            ...(basis.fuelGas.supply.contractMaxDailyQuantity == null
              ? {}
              : { contractMaxDailyQuantity: basis.fuelGas.supply.contractMaxDailyQuantity }),
            ratedAtPressure: { ...basis.fuelGas.supply.ratedAtPressure },
            capacityLimit: basis.fuelGas.supply.capacityLimit,
            ...(basis.fuelGas.supply.compressibility
              ? { compressibility: { ...basis.fuelGas.supply.compressibility } }
              : {}),
            operatingCase: basis.fuelGas.supply.operatingCase,
          },
        } : {}),
      }
    : undefined;
  const acoustics: PublicGenerationProjectDutyExhibitBasis['acoustics'] = basis.acoustics
    ? {
        acousticSources: basis.acoustics.acousticSources.map(source => ({
          id: source.id,
          label: source.label,
          quantity: { scope: source.quantity.scope, count: source.quantity.count },
          operatingCase: source.operatingCase,
          appliedDatum: publicNoiseDatum(source.appliedDatum),
        })),
      }
    : undefined;
  const water: PublicGenerationProjectDutyExhibitBasis['water'] = basis.water == null
    ? undefined
    : basis.water.kind === 'gross-output-intensity'
      ? {
          kind: 'gross-output-intensity',
          withdrawalGalPerMWhGross: basis.water.withdrawalGalPerMWhGross,
          consumptionGalPerMWhGross: basis.water.consumptionGalPerMWhGross,
          operatingCase: basis.water.operatingCase,
        }
      : {
          kind: 'absolute-design-day',
          withdrawalGalPerDay: basis.water.withdrawalGalPerDay,
          consumptionGalPerDay: basis.water.consumptionGalPerDay,
          referenceGrossMWhPerDay: basis.water.referenceGrossMWhPerDay,
          operatingCase: basis.water.operatingCase,
        };
  return {
    basisName: basis.basisName,
    // A user-bound dossier has no catalog row; the exhibit names the entry it
    // is bound to instead of asserting a catalog id it does not have.
    catalogId: basis.catalogBinding?.catalogId
      ?? `user-equipment:${basis.userEquipmentBinding?.entryId ?? 'unknown'}`,
    basisHash: basis.basisHash,
    appliedDutyFingerprint: basis.appliedDutyFingerprint,
    currentDutyFingerprint: projection.currentDutyFingerprint,
    alignmentStatus: projection.alignmentStatus,
    alignmentNotes: [...projection.alignmentNotes],
    ...(performance ? { performance } : {}),
    installedReservations: publicInstalledReservations(basis),
    ...(fuelGas ? { fuelGas } : {}),
    ...(acoustics ? { acoustics } : {}),
    ...(water ? { water } : {}),
    publicSources: basis.sources.flatMap(source => source.disclosure === 'public'
      ? [{
          authorityRole: source.authorityRole,
          publisher: source.publisher,
          title: source.title,
          ...(source.revision ? { revision: source.revision } : {}),
          url: source.url,
          retrievedAt: source.retrievedAt,
        }]
      : []),
    privateSourceCount: basis.sources.filter(source => source.disclosure === 'private').length,
    completeness: structuredClone(projection.completeness),
    gasPressureCheck: structuredClone(projection.gasPressureCheck),
    spatialSummary: structuredClone(projection.spatialSummary),
  };
}

/** Throwing guard for call sites that need a typed, verified dossier. */
export function requireValidGenerationProjectDutyBasis(
  value: unknown,
  scenario?: GenerationImpactScenarioInput,
): GenerationProjectDutyBasis {
  const errors = validateGenerationProjectDutyBasis(value, scenario);
  if (errors.length > 0) {
    throw new TypeError(`Invalid generation project duty basis: ${errors.join('; ')}`);
  }
  return value as GenerationProjectDutyBasis;
}
